/**
 * Act1Dna — DNA Sourcing & Scoring.
 *
 * Choreography (≈3s total):
 *   T+0.0s — Brand fingerprint chips materialize across the top of
 *            the canvas, one at a time (50ms stagger).
 *   T+0.6s — Stream of candidate creators flows in from the right
 *            edge. Each creator passes under the fingerprint chips
 *            and "lights up" the dimensions it matches (a brief glow
 *            on the matched chips while the creator passes through).
 *   T+2.4s — Surviving creators settle into a holding zone in the
 *            lower half. Rejected creators fade out.
 *   T+2.8s — Final state: chips persist (these stay through Acts
 *            2-4). Holding zone shows survivors in a tight cluster.
 *            Narrator updates: "Pulled N from your DNA. M dimensions
 *            matched. K% coverage."
 *
 * The act's data shape from the backend:
 *   fingerprint:  list[{ key, label, detail, source }]
 *   candidates:   list[{ handle, name, dna_score, matched_dims, ... }]
 *   survivors:    list[handle] — strongest DNA matches, sorted desc
 *   coverage_pct: int 0-100
 *
 * NOTE on safe-surface contract: dna_score is shown on each candidate
 * dot but ONLY as a 0-100 match strength — never as projected lift,
 * revenue, or ROAS. The backend route guarantees no forbidden fields
 * exist on this payload (test_wave_briefing_safe_surface.py).
 */
import React, { useEffect, useState, useRef } from 'react';
import BriefingCreatorCard from './BriefingCreatorCard';
import CohortMetricsHero from './CohortMetricsHero';

// Color palette — matches the rest of the WAVE platform.
const TEAL = '#00D4FF';

const CHIP_STAGGER_MS = 80;
const CREATOR_STREAM_DURATION_MS = 1800;
const SETTLE_DURATION_MS = 400;

export default function Act1Dna({
  fingerprint,
  candidates,
  survivors,
  rejectedSample,
  cohortHero,
  tickedRingKeys,
  galleryHidden,
  onComplete,
  isActive,
}) {
  // Stage state machine. Stages compose; later stages don't reset earlier ones.
  //   "chips"     — fingerprint chips fading in (T+0.0 → T+0.6s)
  //   "stream"    — creators streaming + matching (T+0.6 → T+2.4s)
  //   "settled"   — final static state (T+2.8s onward)
  const [stage, setStage] = useState('chips');
  // The currently-displayed spotlight (full creator object + outcome).
  // Driving as a single object lets us pause briefly between firings so
  // the brand can read each card before the next one swaps in.
  const [activeSpotlight, setActiveSpotlight] = useState(null);
  const [activeCreatorIdx, setActiveCreatorIdx] = useState(-1);
  const [glowingChips, setGlowingChips] = useState(new Set());
  const completionFiredRef = useRef(false);

  // Run the timeline whenever this act becomes active.
  // Dependencies intentionally narrow — only `isActive` triggers a
  // restart of the timeline. fingerprint/candidates/onComplete are
  // read via refs so updates from the parent (e.g. narrator updates
  // that cause new function refs) don't tear down the timeline.
  const fingerprintRef = useRef(fingerprint);
  const candidatesRef = useRef(candidates);
  const rejectedRef = useRef(rejectedSample);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { fingerprintRef.current = fingerprint; }, [fingerprint]);
  useEffect(() => { candidatesRef.current = candidates; }, [candidates]);
  useEffect(() => { rejectedRef.current = rejectedSample; }, [rejectedSample]);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    if (!isActive) return undefined;
    const fp = fingerprintRef.current || [];
    const cands = candidatesRef.current || [];
    const rejected = rejectedRef.current || [];
    setStage('chips');
    setActiveSpotlight(null);
    setActiveCreatorIdx(-1);
    setGlowingChips(new Set());
    completionFiredRef.current = false;

    const chipPhaseEnd = fp.length * CHIP_STAGGER_MS + 200;
    const streamPhaseStart = chipPhaseEnd;
    const streamPhaseEnd = streamPhaseStart + CREATOR_STREAM_DURATION_MS;
    const settledStart = streamPhaseEnd + SETTLE_DURATION_MS;

    const timers = [];
    timers.push(setTimeout(() => setStage('stream'), streamPhaseStart));

    // Stream the creators — 12 evenly-spaced "spotlights" so the user
    // perceives motion even when there are 150+ candidates. Every 3rd
    // pass is a REJECTION beat — pulled from the backend's
    // rejected_sample (lowest-DNA creators) — so the brand visibly
    // feels the filter culling. The other 8-9 are MATCH beats from
    // the top candidates.
    const SPOTLIGHTS = 12;
    const interval = CREATOR_STREAM_DURATION_MS / SPOTLIGHTS;
    let rejectIdx = 0;
    for (let i = 0; i < SPOTLIGHTS; i++) {
      const isRejection = (i % 3 === 1) && rejected.length > 0;
      let spotlightCreator;
      let outcome;
      if (isRejection) {
        spotlightCreator = rejected[rejectIdx % rejected.length];
        rejectIdx += 1;
        outcome = 'rejected';
      } else {
        const idx = Math.floor((i / SPOTLIGHTS) * cands.length);
        spotlightCreator = cands[idx];
        outcome = 'matched';
      }
      const matchedDims = spotlightCreator?.matched_dims || [];

      timers.push(setTimeout(() => {
        setActiveSpotlight({ creator: spotlightCreator, outcome });
        setActiveCreatorIdx(i);
        // Only matched beats light up the fingerprint chips — rejected
        // creators don't get to "claim" any dimension.
        setGlowingChips(outcome === 'matched' ? new Set(matchedDims) : new Set());
        timers.push(setTimeout(
          () => setGlowingChips(new Set()),
          interval * 0.8,
        ));
      }, streamPhaseStart + i * interval));
    }

    // Settle into final state
    timers.push(setTimeout(() => {
      setStage('settled');
      setActiveSpotlight(null);
      setActiveCreatorIdx(-1);
      setGlowingChips(new Set());
    }, settledStart));

    // Fire onComplete once when act has fully settled
    timers.push(setTimeout(() => {
      if (!completionFiredRef.current) {
        completionFiredRef.current = true;
        onCompleteRef.current?.();
      }
    }, settledStart + 200));

    return () => timers.forEach(clearTimeout);
  }, [isActive]);

  // Survivors live in a deterministic positions in the holding zone.
  // Layout: 5 columns × N rows packed near the center-lower portion of
  // the canvas. We render the TOP 40 survivors (UI-bounded) — the
  // backend may ship more, but 40 is the visual sweet spot.
  const visibleSurvivors = survivors.slice(0, 40);

  return (
    <div
      data-testid="briefing-act-1"
      data-stage={stage}
      className={`flex flex-col px-6 ${galleryHidden ? 'pt-3' : 'flex-1 pt-5'}`}
    >
      {/* ── Fingerprint chips row — ELEVATED. These are the brand's
            data-room context (the "why" behind every cohort metric
            below). Larger presence than the prior small boxes. They
            persist through Acts 2-4 as the canvas frame. */}
      <div
        data-testid="briefing-fingerprint-row"
        className="grid grid-cols-4 gap-3 mb-4 min-h-[78px]"
      >
        {fingerprint.length === 0 && (
          <div
            data-testid="briefing-no-fingerprint"
            className="col-span-4 text-[10px] uppercase tracking-[0.18em] font-bold text-gray-600 px-3 py-3 rounded-xl border border-white/5"
          >
            No 1st-party data · matching by vertical only
          </div>
        )}
        {fingerprint.map((chip, i) => {
          const glowing = glowingChips.has(chip.source);
          return (
            <div
              key={chip.key}
              data-testid={`fingerprint-chip-${chip.source}`}
              data-glowing={glowing ? 'true' : 'false'}
              className="rounded-xl px-4 py-3 transition-all duration-200"
              style={{
                background: glowing
                  ? `linear-gradient(135deg, ${TEAL}22, ${TEAL}0A)`
                  : 'rgba(255,255,255,0.025)',
                border: `1px solid ${glowing ? TEAL : 'rgba(255,255,255,0.07)'}`,
                boxShadow: glowing ? `0 0 22px ${TEAL}55` : 'none',
                animation: `briefingChipFade 400ms ease-out both`,
                animationDelay: `${i * CHIP_STAGGER_MS}ms`,
              }}
            >
              <p
                className="text-[9px] uppercase tracking-[0.18em] font-black mb-1"
                style={{ color: glowing ? TEAL : '#9BA1A6' }}
              >
                {chip.label}
              </p>
              <p className="text-[12.5px] text-white font-bold leading-snug line-clamp-2">
                {chip.detail}
              </p>
            </div>
          );
        })}
      </div>

      {/* ── Cohort Metrics Hero — 5 tiles that answer the SVP's
            first-three-seconds questions about the cohort. Sits
            between the brand context (above) and the creator gallery
            (below), eliminating the dead space the prior layout had. */}
      <CohortMetricsHero
        hero={{ ...cohortHero, creator_count: visibleSurvivors.length }}
        tickedRingKeys={tickedRingKeys}
      />

      {/* ── Creator stream + Holding zone ─────────────────────── */}
      {/* Hidden once Act 2 begins — the radial network REPLACES the
          gallery, the same shields just reorganize into the network.
          We don't render two layers of shields at once. */}
      <div
        className="relative flex-1 min-h-[400px] rounded-2xl overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at center bottom, rgba(0,212,255,0.06), transparent 70%)',
          border: '1px solid rgba(255,255,255,0.04)',
          display: galleryHidden ? 'none' : 'block',
        }}
      >
        {/* Streaming creators — only visible during 'stream' stage.
            The new outcome-aware spotlight cycles between MATCHED
            (teal pulse, slides into holding) and REJECTED (red flash,
            fades out). Rejection beats are pulled from the backend's
            rejected_sample (lowest-DNA candidates) so the brand
            visibly feels the filter actively culling people, not
            silently removing them. */}
        {stage === 'stream' && activeSpotlight && (
          <div
            data-testid="briefing-stream-spotlight"
            data-outcome={activeSpotlight.outcome}
            // key forces full unmount/remount each spotlight so the
            // animation keyframes restart cleanly (otherwise a single
            // <BriefingCreatorCard> would just update its props and
            // skip the rise-in).
            key={`spot-${activeCreatorIdx}-${activeSpotlight.creator?.handle}`}
            className="absolute top-4 left-1/2 -translate-x-1/2 z-10"
          >
            <BriefingCreatorCard
              creator={activeSpotlight.creator}
              spotlight
              outcome={activeSpotlight.outcome}
            />
          </div>
        )}

        {/* Holding zone — survivors arrive as shield-shaped face
            galleries. The hero above carries the cohort intelligence;
            this zone is faces + names + handles only. */}
        <div
          data-testid="briefing-holding-zone"
          className="absolute inset-x-4 bottom-4 top-32 grid grid-cols-10 gap-x-2 gap-y-4 content-start overflow-y-auto pr-1"
        >
          {visibleSurvivors.map((handle, i) => {
            const creator = candidates.find(c => c.handle === handle);
            if (!creator) return null;
            // Survivors appear progressively across the stream phase.
            // We map each survivor to a "spotlight checkpoint" — once
            // the streaming spotlight has passed `i / total` of the
            // way through, this survivor is "arrived" (full opacity).
            // During 'chips' stage: all dim. During 'settled': all visible.
            let arrived;
            if (stage === 'chips') arrived = false;
            else if (stage === 'settled') arrived = true;
            else {
              const SPOTLIGHTS = 12;
              const candidateProgress = candidates.length === 0
                ? 0
                : activeCreatorIdx / candidates.length;
              const arrivalThreshold = i / Math.max(1, visibleSurvivors.length - 1);
              arrived = candidateProgress >= arrivalThreshold * (SPOTLIGHTS - 1) / SPOTLIGHTS;
            }
            return (
              <BriefingCreatorCard
                key={handle}
                creator={creator}
                dim={!arrived}
              />
            );
          })}
        </div>
      </div>

      {/* Inline keyframes — scoped here so the act is self-contained */}
      <style>{`
        @keyframes briefingChipFade {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes briefingCardRise {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        /* MATCHED spotlight — rises in, holds briefly, slides DOWN
           toward the holding zone before its next sibling appears.
           The downward translate sells "this creator joined the
           cohort" without needing an explicit DOM move. */
        @keyframes briefingSpotlightMatch {
          0%   { opacity: 0; transform: translateY(-12px) scale(0.92); }
          20%  { opacity: 1; transform: translateY(0)     scale(1.04); }
          70%  { opacity: 1; transform: translateY(0)     scale(1.00); }
          100% { opacity: 0; transform: translateY(40px)  scale(0.86); }
        }
        /* REJECTED spotlight — rises in, holds, then the red border
           flashes brighter twice and the card collapses + fades out.
           The double-pulse on the border is the "filter actively
           rejecting" beat the brand needs to see. */
        @keyframes briefingSpotlightReject {
          0%   { opacity: 0; transform: translateY(-12px) scale(0.92); filter: brightness(1); }
          15%  { opacity: 1; transform: translateY(0)     scale(1.00); filter: brightness(1); }
          35%  { filter: brightness(1.6); }
          50%  { filter: brightness(1.0); }
          65%  { filter: brightness(1.8); }
          80%  { filter: brightness(1.0); }
          100% { opacity: 0; transform: translateY(0)     scale(0.78); filter: brightness(0.7); }
        }
      `}</style>
    </div>
  );
}
